import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { SystemEntity } from '@erp/shared-types';
import { useMemo } from 'react';

export const RELEVANT_ENTITY_TYPES: readonly SystemEntity[] = [
  SystemEntity.AP_INVOICE,
  SystemEntity.INTERNAL_PURCHASE_ORDER,
  SystemEntity.PAYMENT,
  SystemEntity.OUTBOUND_EMAIL,
  SystemEntity.AR_SALES_ORDER,
  SystemEntity.SCM_SHIPMENT,
  // AR invoice detail tabs (derived associations)
  SystemEntity.AR_INVOICE,
  SystemEntity.AR_PAYMENT,
] as const;

function isRelevantEntityType(entityType: string): boolean {
  return (RELEVANT_ENTITY_TYPES as readonly string[]).includes(entityType);
}

export function useInvoiceAssociations(invoiceId: string | undefined) {
  const query = useQuery({
    ...orpc.core.entityAssociations.list.queryOptions({
      input: {
        entityId: invoiceId || '',
        entityType: SystemEntity.AP_INVOICE,
      },
    }),
    enabled: !!invoiceId,
  });

  const filteredAssociations = useMemo(() => {
    if (!query.data?.associations) return [];
    return query.data.associations.filter((assoc) => {
      return isRelevantEntityType(assoc.entity.entityType);
    });
  }, [query.data?.associations]);

  return {
    ...query,
    associations: filteredAssociations,
  };
}

export function usePurchaseOrderAssociations(
  purchaseOrderId: string | undefined,
) {
  const query = useQuery({
    ...orpc.core.entityAssociations.list.queryOptions({
      input: {
        entityId: purchaseOrderId || '',
        entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
      },
    }),
    enabled: !!purchaseOrderId,
  });

  const filteredAssociations = useMemo(() => {
    if (!query.data?.associations) return [];
    return query.data.associations.filter((assoc) => {
      return isRelevantEntityType(assoc.entity.entityType);
    });
  }, [query.data?.associations]);

  return {
    ...query,
    associations: filteredAssociations,
  };
}

export function usePaymentAssociations(paymentId: string | undefined) {
  const query = useQuery({
    ...orpc.core.entityAssociations.list.queryOptions({
      input: {
        entityId: paymentId || '',
        entityType: SystemEntity.PAYMENT,
      },
    }),
    enabled: !!paymentId,
  });

  const filteredAssociations = useMemo(() => {
    if (!query.data?.associations) return [];
    return query.data.associations.filter((assoc) => {
      return isRelevantEntityType(assoc.entity.entityType);
    });
  }, [query.data?.associations]);

  return {
    ...query,
    associations: filteredAssociations,
  };
}

export function useSalesOrderAssociations(salesOrderId: string | undefined) {
  const query = useQuery({
    ...orpc.core.entityAssociations.list.queryOptions({
      input: {
        entityId: salesOrderId || '',
        entityType: SystemEntity.AR_SALES_ORDER,
      },
    }),
    enabled: !!salesOrderId,
  });

  const filteredAssociations = useMemo(() => {
    if (!query.data?.associations) return [];
    return query.data.associations.filter((assoc) => {
      return isRelevantEntityType(assoc.entity.entityType);
    });
  }, [query.data?.associations]);

  return {
    ...query,
    associations: filteredAssociations,
  };
}

export function useShipmentAssociations(shipmentId: string | undefined) {
  const query = useQuery({
    ...orpc.core.entityAssociations.list.queryOptions({
      input: {
        entityId: shipmentId || '',
        entityType: SystemEntity.SCM_SHIPMENT,
      },
    }),
    enabled: !!shipmentId,
  });

  const filteredAssociations = useMemo(() => {
    if (!query.data?.associations) return [];
    return query.data.associations.filter((assoc) => {
      return isRelevantEntityType(assoc.entity.entityType);
    });
  }, [query.data?.associations]);

  return {
    ...query,
    associations: filteredAssociations,
  };
}
